/**
 * Trena (Orçamentador Auditável L2) · schemas Zod · 2026-05-11
 *
 * Tipos compartilhados entre API e Web pra request/response.
 * Mantém PARALELO ao schema D1 (migrations 0172-0175) mas sem expor
 * detalhes de persistência (timestamps são strings ISO, status são
 * enums Zod fechados).
 */
import { z } from 'zod';

// ─── Enums ─────────────────────────────────────────────────────────────

export const trenaConfidenceLevelSchema = z.enum(['L1', 'L2', 'L3', 'L4', 'L5']);
export type TrenaConfidenceLevel = z.infer<typeof trenaConfidenceLevelSchema>;

export const trenaEstimateStatusSchema = z.enum([
  'draft',
  'calculated',
  'reviewed',
  'approved',
  'exported',
  'archived',
]);
export type TrenaEstimateStatus = z.infer<typeof trenaEstimateStatusSchema>;

export const trenaItemStatusSchema = z.enum([
  'estimated',
  'reviewed',
  'confirmed',
  'pending',
  'conflicted',
  'manual',
  'superseded',
]);
export type TrenaItemStatus = z.infer<typeof trenaItemStatusSchema>;

export const trenaItemSourceSchema = z.enum([
  'composition',
  'manual',
  'imported',
]);
export type TrenaItemSource = z.infer<typeof trenaItemSourceSchema>;

export const trenaMeasurementSourceSchema = z.enum([
  'informado',
  'extraido',
  'calibrado_planta',
  'calculado',
]);
export type TrenaMeasurementSource = z.infer<typeof trenaMeasurementSourceSchema>;

export const trenaAssumptionSourceSchema = z.enum([
  'informado',
  'extraido_memorial',
  'padrao_sistema',
  'calculado',
]);
export type TrenaAssumptionSource = z.infer<typeof trenaAssumptionSourceSchema>;

export const trenaConfidenceSchema = z.enum(['baixa', 'media', 'alta']);
export type TrenaConfidence = z.infer<typeof trenaConfidenceSchema>;

export const trenaRoundingStrategySchema = z.enum([
  'ceil_unit',
  'ceil_package',
  'decimal_2',
  'nearest_package',
]);
export type TrenaRoundingStrategy = z.infer<typeof trenaRoundingStrategySchema>;

export const trenaCompositionSourceSchema = z.enum([
  'sistema_padrao',
  'tenant_custom',
  'user_clone',
  'imported',
]);
export type TrenaCompositionSource = z.infer<typeof trenaCompositionSourceSchema>;

export const trenaCompositionConfidenceSchema = z.enum([
  'referencia_inicial',
  'validada',
  'calibrada_obra',
]);
export type TrenaCompositionConfidence = z.infer<typeof trenaCompositionConfidenceSchema>;

// ─── Domain models ─────────────────────────────────────────────────────

export const trenaCompositionInputSchema = z.object({
  id: z.string().min(1),
  compositionId: z.string().min(1),
  insumoId: z.string().min(1),
  quantityPerUnit: z.number().positive(),
  unit: z.string().min(1).max(30),
  wasteFactor: z.number().min(0).max(2),
  sortOrder: z.number().int().nonnegative(),
  notes: z.string().nullable().optional(),
});
export type TrenaCompositionInput = z.infer<typeof trenaCompositionInputSchema>;

export const trenaCompositionSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  code: z.string().min(1).max(120),
  version: z.number().int().min(1),
  name: z.string().min(2).max(240),
  description: z.string().nullable().optional(),
  baseUnit: z.string().min(1).max(20),
  category: z.string().nullable().optional(),
  source: trenaCompositionSourceSchema,
  confidence: trenaCompositionConfidenceSchema,
  editable: z.boolean(),
  region: z.string().nullable().optional(),
  isSystem: z.boolean(),
  parentCompositionId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
  archivedAt: z.string().datetime({ offset: true }).nullable().optional(),
  inputs: z.array(trenaCompositionInputSchema).optional(),
});
export type TrenaComposition = z.infer<typeof trenaCompositionSchema>;

export const trenaRoomSchema = z.object({
  id: z.string().min(1),
  estimateId: z.string().min(1),
  name: z.string().min(1).max(120),
  function: z.string().nullable().optional(),
  sortOrder: z.number().int().nonnegative(),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
});
export type TrenaRoom = z.infer<typeof trenaRoomSchema>;

export const trenaMeasurementScopeSchema = z.enum([
  'projeto',
  'ambiente',
  'externa',
]);
export type TrenaMeasurementScope = z.infer<typeof trenaMeasurementScopeSchema>;

export const trenaMeasurementSchema = z.object({
  id: z.string().min(1),
  estimateId: z.string().min(1),
  roomId: z.string().min(1).nullable().optional(),
  scope: trenaMeasurementScopeSchema,
  key: z.string().min(1).max(80),
  value: z.number().nonnegative(),
  unit: z.string().min(1).max(20),
  source: trenaMeasurementSourceSchema,
  confidence: trenaConfidenceSchema,
  status: z.enum([
    'estimated',
    'reviewed',
    'confirmed',
    'needs_review',
    'conflicted',
  ]),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type TrenaMeasurement = z.infer<typeof trenaMeasurementSchema>;

export const trenaAssumptionSchema = z.object({
  id: z.string().min(1),
  estimateId: z.string().min(1),
  key: z.string().min(1).max(80),
  value: z.string().max(400),
  unit: z.string().nullable().optional(),
  source: trenaAssumptionSourceSchema,
  confidence: trenaConfidenceSchema,
  status: z.enum([
    'estimated',
    'reviewed',
    'confirmed',
    'needs_review',
    'conflicted',
  ]),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type TrenaAssumption = z.infer<typeof trenaAssumptionSchema>;

export const trenaCalculationSnapshotSchema = z.object({
  composition: z.object({
    id: z.string(),
    code: z.string(),
    version: z.number().int(),
    name: z.string(),
    category: z.string().nullable(),
  }),
  inputs: z.array(
    z.object({
      insumoId: z.string(),
      descriptionAtCalc: z.string(),
      qtyPerUnit: z.number(),
      unit: z.string(),
      wasteFactor: z.number(),
    }),
  ),
  assumptions: z.array(
    z.object({
      id: z.string(),
      key: z.string(),
      valueAtCalc: z.string(),
      unit: z.string().nullable().optional(),
      source: trenaAssumptionSourceSchema,
    }),
  ),
  baseMeasurement: z.object({
    value: z.number(),
    unit: z.string(),
    source: trenaMeasurementSourceSchema,
    measurementId: z.string().optional(),
    confidenceAtCalc: trenaConfidenceSchema.optional(),
  }),
  calculationSteps: z.array(z.string()),
  rounding: z.object({
    strategy: trenaRoundingStrategySchema,
    inputQty: z.number(),
    result: z.number(),
    packageSize: z.number().nullable().optional(),
    packageUnit: z.string().nullable().optional(),
  }),
  finalQuantity: z.number().nonnegative(),
  finalUnit: z.string(),
  unitPriceCentsAtCalc: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  confidenceLevel: trenaConfidenceLevelSchema,
  generatedAt: z.string().datetime({ offset: true }),
  engineVersion: z.string(),
});
export type TrenaCalculationSnapshot = z.infer<typeof trenaCalculationSnapshotSchema>;

export const trenaItemSchema = z.object({
  id: z.string().min(1),
  estimateId: z.string().min(1),
  compositionId: z.string().nullable().optional(),
  insumoId: z.string().nullable().optional(),
  source: trenaItemSourceSchema,
  status: trenaItemStatusSchema,
  confidenceLevel: trenaConfidenceLevelSchema,
  description: z.string().min(1).max(400),
  category: z.string().nullable().optional(),
  unit: z.string().min(1).max(30),
  quantityRaw: z.number().nullable().optional(),
  quantityFinal: z.number().nonnegative(),
  unitPriceCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  calculationSnapshot: trenaCalculationSnapshotSchema,
  budgetItemId: z.string().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type TrenaItem = z.infer<typeof trenaItemSchema>;

export const trenaEstimateSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  projectId: z.string().min(1),
  name: z.string().min(2).max(180),
  confidenceLevel: trenaConfidenceLevelSchema,
  status: trenaEstimateStatusSchema,
  totalCents: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().nullable().optional(),
  parentEstimateId: z.string().nullable().optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  calculatedAt: z.string().datetime({ offset: true }).nullable().optional(),
  approvedAt: z.string().datetime({ offset: true }).nullable().optional(),
  approvedBy: z.string().nullable().optional(),
  archivedAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type TrenaEstimate = z.infer<typeof trenaEstimateSchema>;

// ─── Request shapes ────────────────────────────────────────────────────

export const createTrenaEstimateRequestSchema = z.object({
  name: z.string().trim().min(2).max(180),
  confidenceLevel: trenaConfidenceLevelSchema.default('L2'),
  notes: z.string().trim().max(2000).optional(),
});
export type CreateTrenaEstimateRequest = z.infer<typeof createTrenaEstimateRequestSchema>;

export const createTrenaRoomRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  function: z.string().trim().max(60).optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  notes: z.string().trim().max(500).optional(),
});
export type CreateTrenaRoomRequest = z.infer<typeof createTrenaRoomRequestSchema>;

export const upsertTrenaMeasurementRequestSchema = z.object({
  roomId: z.string().min(1).nullable().optional(),
  scope: trenaMeasurementScopeSchema,
  key: z.string().trim().min(1).max(80),
  value: z.number().nonnegative(),
  unit: z.string().trim().min(1).max(20),
  source: trenaMeasurementSourceSchema.default('informado'),
  confidence: trenaConfidenceSchema.default('media'),
  notes: z.string().trim().max(500).optional(),
});
export type UpsertTrenaMeasurementRequest = z.infer<typeof upsertTrenaMeasurementRequestSchema>;

export const upsertTrenaAssumptionRequestSchema = z.object({
  key: z.string().trim().min(1).max(80),
  value: z.string().trim().min(1).max(400),
  unit: z.string().trim().max(20).optional(),
  source: trenaAssumptionSourceSchema.default('informado'),
  confidence: trenaConfidenceSchema.default('media'),
  notes: z.string().trim().max(500).optional(),
});
export type UpsertTrenaAssumptionRequest = z.infer<typeof upsertTrenaAssumptionRequestSchema>;

/** Pra calcular · seleciona quais composições rodam contra quais medidas/ambientes */
export const calculateTrenaEstimateRequestSchema = z.object({
  /**
   * Lista de jobs · cada um é (compositionId × {measurementId OU roomId}) → estimate_items.
   * 2026-05-12 · W2 · roomId é alternativa a measurementId · backend resolve
   * usando room.area_m2 como base. Útil pra obras multi-material onde cada
   * ambiente tem piso/cobertura diferente.
   */
  jobs: z
    .array(
      z.object({
        compositionId: z.string().min(1),
        measurementId: z.string().min(1).optional(),
        roomId: z.string().min(1).optional(),
        /**
         * W3 · 2026-05-13 · FIN2 · qty manual pra esquadrias (portas: un,
         * janelas: m² de abertura). Quando setado em conjunto com roomId,
         * substitui area/perímetro do ambiente como base measurement.
         */
        customQuantity: z.number().positive().optional(),
        /** Se PM marcou item como manual, pula composição e usa esses campos. */
        manualOverride: z
          .object({
            insumoId: z.string().min(1),
            quantity: z.number().positive(),
            unit: z.string().min(1).max(20),
            unitPriceCents: z.number().int().nonnegative(),
            description: z.string().trim().min(1).max(400),
            category: z.string().trim().max(60).optional(),
          })
          .optional(),
      }).refine(
        (j) => j.measurementId || j.roomId || j.customQuantity || j.manualOverride,
        { message: 'Job precisa de measurementId, roomId, customQuantity ou manualOverride.' },
      ),
    )
    .min(1)
    .max(500),
  /**
   * Wave ppppp · 2026-05-16 · Resolução de conflitos da etapa de leitura.
   *
   * Mapa de `itemKey` → `docId`. Quando o consolidador detecta múltiplas
   * fontes para a mesma chave canônica, o PM escolhe uma e o wizard envia
   * essa escolha aqui para que o backend aplique ANTES de rodar a gate de
   * compat / sanity. Sem isto, "Manter apenas esta" não desbloqueia o
   * cálculo porque o servidor continua reportando o conflito.
   *
   * Chave normalizada: o prefixo `m:` é opcional (o frontend usa `m:<key>`
   * como identificador interno do wizard). Valores são docIds (24 chars max
   * em geral; aceitamos string.min(1)). Limite 500 entradas para limitar o
   * tamanho do payload.
   */
  conflictResolutions: z
    .record(z.string().min(1).max(120), z.string().min(1).max(120))
    .optional(),
});
export type CalculateTrenaEstimateRequest = z.infer<typeof calculateTrenaEstimateRequestSchema>;

export const commitTrenaToBudgetRequestSchema = z.object({
  /** Se true, sobrescreve budget_items existentes que vieram de outro source (manual, imported). */
  overwriteManual: z.boolean().default(false),
  /** Apenas committa items com este status. */
  onlyStatus: z.array(trenaItemStatusSchema).optional(),
});
export type CommitTrenaToBudgetRequest = z.infer<typeof commitTrenaToBudgetRequestSchema>;

export const commitTrenaToBudgetResponseSchema = z.object({
  estimateId: z.string(),
  budgetItemsCreated: z.number().int().nonnegative(),
  budgetItemsUpdated: z.number().int().nonnegative(),
  budgetItemsSkipped: z.number().int().nonnegative(),
  totalCentsCommitted: z.number().int().nonnegative(),
});
export type CommitTrenaToBudgetResponse = z.infer<typeof commitTrenaToBudgetResponseSchema>;
